import {
    CircularProgress,
    createStyles,
    Fab,
    Theme,
    Typography,
    withStyles,
    WithStyles,
    Zoom
} from "@material-ui/core";
import "moment-timezone";
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { IParcelBid } from "../../../models/parcel/IParcelBid";
import { BidLoadState, LoadState } from "../../../models/utility/LoadState";
import { Numbers } from "../../../models/utility/Numbers";
import { IAppDispatch, IAppState } from "../../../store";
import * as ParcelBidStore from "../../../store/ParcelBidStore";
import * as UserStore from "../../../store/UserStore";
import * as GlobalTheme from "../../../styles/index";
import * as ParcelBidNodeCss from "../../../styles/ParcelBidNodeCss";
import ParcelBidHistoryNode from "./ParcelBidHistoryNode";

type ParcelBidHistoryProps = ParcelBidStore.IParcelBidStoreState &
    UserStore.IUserStoreState &
    typeof ParcelBidStore.actionCreators &
    typeof UserStore.actionCreators &
    WithStyles<typeof styles | typeof ParcelBidNodeCss.styles> & {
        bidAmount: number;
        bidIncrement: number;
        canPlaceBid: boolean;
        parcelId: string;
    };

const styles = (theme: Theme) =>
    createStyles({
        auctionStartText: { color: theme.palette.common.white },
        circleStart: {
            [theme.breakpoints.down("xs")]: {
                height: 12,
                width: 12
            },
            borderRadius: "50%",
            height: 15,
            position: "relative",
            width: 15
        },
        loaderWrapper: {
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            margin: theme.spacing(3),
            width: "100%"
        },
        ...ParcelBidNodeCss.styles(theme)
    });

class ParcelBidHistory extends React.Component<ParcelBidHistoryProps, {}> {
    private rootRef = React.createRef<HTMLDivElement>();

    public componentDidMount() {
        window.addEventListener("deviceorientation", this.resizeRootDiv);
        window.addEventListener("orientationchange", this.resizeRootDiv);
        window.addEventListener("resize", this.resizeRootDiv);

        this.resizeRootDiv();
    }

    public componentDidUpdate() {
        this.scrollRootDivRight();
    }

    public componentWillUnmount() {
        window.removeEventListener("deviceorientation", this.resizeRootDiv);
        window.removeEventListener("orientationchange", this.resizeRootDiv);
        window.removeEventListener("resize", this.resizeRootDiv);
    }

    public shouldComponentUpdate(prevProps: ParcelBidHistoryProps) {
        if (
            prevProps.bidAmount !== this.props.bidAmount ||
            prevProps.bidIncrement !== this.props.bidIncrement ||
            prevProps.canPlaceBid !== this.props.canPlaceBid ||
            prevProps.parcelId !== this.props.parcelId ||
            prevProps.parcelBidDict[prevProps.parcelId] !==
                this.props.parcelBidDict[this.props.parcelId] ||
            prevProps.parcelBidLoadState !== this.props.parcelBidLoadState ||
            prevProps.user !== this.props.user
        ) {
            return true;
        }

        return false;
    }

    public render() {
        const { classes, parcelBidDict, parcelBidLoadState, parcelId } =
            this.props;
        const parcelBids = parcelBidDict[parcelId] || [];

        if (parcelBidLoadState !== LoadState.Loaded) {
            return (
                <div className={classes.root} ref={this.rootRef}>
                    <div className={classes.loaderWrapper}>
                        <CircularProgress color="secondary" />
                    </div>
                </div>
            );
        }

        return (
            <div className={classes.root} ref={this.rootRef}>
                {this.renderAuctionStart()}
                {parcelBids.map(this.renderBid)}
                {this.renderBidButton()}
            </div>
        );
    }

    private renderAuctionStart = () => {
        const classes = this.props.classes;

        return (
            <div className={classes.bidNodeWrapper}>
                {this.renderTimeLine("start")}
                <div className={classes.bidBlockTop}>
                    <Typography
                        className={`${classes.textBase} ${classes.textTop} ${classes.auctionStartText}`}
                        component="div"
                        variant="button"
                    >
                        Auction Start
                    </Typography>
                </div>
                <div className={classes.bidBlockMiddle}>
                    <div className={classes.circleStart} />
                </div>
                <div className={classes.bidBlockBottom} />
            </div>
        );
    };

    private renderBid = (bid: IParcelBid, idx: number) => {
        const { canPlaceBid, parcelBidDict, parcelId, user } = this.props;

        const parcelBids = parcelBidDict[parcelId] || [];
        const isCurrentUser = !!(user && user.id === bid.userId);
        const bidCount = parcelBids.length;

        const bidPosition =
            isCurrentUser && idx === bidCount - 1
                ? "end"
                : !canPlaceBid && idx === bidCount - 1
                ? "end"
                : "middle";

        return (
            <ParcelBidHistoryNode
                key={bid.bidAmount}
                bid={bid}
                bidPosition={bidPosition}
                isCurrentUser={isCurrentUser}
            />
        );
    };

    private renderTimeLine = (position: "start" | "middle" | "end") => {
        const classes = this.props.classes;

        let wrapperClasses = classes.timeline;
        let lineClasses = classes.timelineTopLine;

        if (position === "start") {
            wrapperClasses += ` ${classes.timelineStart}`;
            lineClasses += ` ${classes.timelineStartOrEndLine} ${classes.timelineStartLine}`;
        } else if (position === "end") {
            wrapperClasses += ` ${classes.timelineEnd}`;
            lineClasses += ` ${classes.timelineStartOrEndLine} ${classes.timelineEndLine}`;
        }

        return (
            <div className={wrapperClasses}>
                <div className={lineClasses} />
            </div>
        );
    };

    private renderBidButton = () => {
        const {
            bidAmount,
            bidIncrement,
            canPlaceBid,
            classes,
            parcelBidDict,
            parcelId,
            user
        } = this.props;

        const parcelBids = parcelBidDict[parcelId] || [];
        const bidCount = parcelBids.length;
        const lastParcel = bidCount > 0 ? parcelBids[bidCount - 1] : null;

        const newBidIncrement = bidCount > 0 ? bidIncrement : 0;
        const newBidAmt = bidAmount + newBidIncrement;

        return (
            <Zoom
                in={
                    !!user &&
                    canPlaceBid &&
                    (!lastParcel || lastParcel.userId !== user.id)
                }
            >
                <div className={classes.bidNodeWrapper}>
                    {this.renderTimeLine("end")}

                    <div className={classes.bidBlockTop} />
                    <div className={classes.bidBlockMiddle}>
                        <Fab
                            className={classes.bidButton}
                            variant="circular"
                            color="primary"
                            size="small"
                            onClick={this.openBidDialog}
                        >
                            Bid
                        </Fab>
                    </div>
                    <div className={classes.bidBlockBottom}>
                        <Typography
                            className={`${classes.textTime} ${classes.textBase} ${classes.textBidNext}`}
                            component="div"
                            variant="caption"
                            color="primary"
                        >
                            {Numbers.toShortFormat(newBidAmt)}
                        </Typography>
                    </div>
                </div>
            </Zoom>
        );
    };

    private openBidDialog = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        this.props.toggleDialogVisible(true);
        this.props.updateCurrentBid(
            this.props.parcelId,
            BidLoadState.NotSubmitted
        );
    };

    private resizeRootDiv = () => {
        const root = this.rootRef.current;
        if (root) {
            // set proper width
            const maxBodyWidth =
                GlobalTheme.maxBodyWidth + GlobalTheme.desktopPadding * 2;

            const viewWidth = window.innerWidth;
            let newWidth = viewWidth - 310;

            if (viewWidth < maxBodyWidth && viewWidth >= 960) {
                newWidth = viewWidth - Math.round(viewWidth * 0.28);
            } else if (viewWidth < 960 && viewWidth >= 600) {
                newWidth = viewWidth - 80;
            } else if (viewWidth < 600) {
                newWidth = viewWidth - 50;
            }

            root.setAttribute("style", `width: ${newWidth}px;`);
        }

        this.scrollRootDivRight();
    };

    private scrollRootDivRight = () => {
        const root = this.rootRef.current;
        if (root) {
            // scroll as far right as possible
            setTimeout(() => (root.scrollLeft += 10000), 500);
        }
    };
}

const reduxState = (state: IAppState) =>
    Object.assign({}, state.parcelBidStoreState, state.userStoreState);
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(
        Object.assign(
            {},
            ParcelBidStore.actionCreators,
            UserStore.actionCreators
        ),
        dispatch
    );

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(ParcelBidHistory)
);
